// Stake tables on the phone (spec FE-04, PF-07). Lagos Life takes the fare on its own sheet and
// holds the pot; we only ever bind the match it gives us to our stake park, and our server checks
// that with Lagos Life. Once the match is in play we bind once more, which tells the park exactly
// who staked so the round can start.
import { type VatarMatchView, vatar } from '@wahala/vatar-client';
import { type StakeTable, useUi } from '../store/ui';
import { S } from '../strings';
import { ApiError, api, type JoinTicket } from './api';

/** Our own limits (PF-15): a stake ban, today's fare cap, or the leave rules' wait. */
const OUR_LIMITS = new Set(['stake_banned', 'stake_cap', 'leave_cooldown']);

/** Ask our server first, so Lagos Life's sheet never takes fare for a table we would refuse. */
export async function stakeAllowed(stake: number): Promise<void> {
  if (stake > 0) await api.stakeAllowed(stake);
}

let watching: string | null = null;
let told = false;
let off: (() => void) | null = null;

export async function bindStake(m: VatarMatchView): Promise<JoinTicket> {
  let ticket: JoinTicket;
  try {
    ticket = await api.bindStake(m.id);
  } catch (e) {
    // refused by our own limits: leave the table while it is still filling, so the fare comes back
    if (e instanceof ApiError && OUR_LIMITS.has(e.code) && m.status === 'open')
      await vatar.match.leave().catch(() => {});
    throw e;
  }
  watch(m.id);
  if (m.status === 'playing') told = true;
  table(m);
  return ticket;
}

/** What the stake park's lobby shows about the table: Lagos Life's own word, as it last came. */
function table(m: VatarMatchView): void {
  const t: StakeTable = {
    id: m.id,
    code: m.code ?? '',
    status: m.status,
    seats: m.seats,
    seated: m.players.length,
    stake: m.stake,
  };
  useUi.getState().set({ stakeTable: t });
}

/**
 * A table that will never play (PF-07): it didn't fill in time (void) or was called off (cancelled).
 * Lagos Life gives every fare back; a player still waiting in the stake park goes back to the menu
 * and is told so. A round already played is left alone (its results board says how it ended).
 */
function ended(m: VatarMatchView): void {
  const ui = useUi.getState();
  if (m.status !== 'void' && m.status !== 'cancelled') {
    ui.set({ stakeTable: null });
    return;
  }
  const park = ui.park;
  const lobby = park?.world.lobby;
  if (park && lobby?.stakeReady !== undefined && lobby.phase === 'WAITING') park.leave();
  ui.set({
    stakeTable: null,
    notice: m.status === 'void' ? S.stakes.voided(m.code ?? '') : S.stakes.cancelled(m.code ?? ''),
    noticeTone: 'info',
  });
}

/**
 * Leaving the stake park before the table is in play leaves the Lagos Life table too, so the fare
 * comes back (the note on the Stake tables tab promises it). Once it is in play the seat is held.
 */
export async function leaveStakeTable(): Promise<void> {
  const t = useUi.getState().stakeTable;
  if (!t || t.status !== 'open') return;
  useUi.getState().set({ stakeTable: null });
  off?.();
  off = null;
  watching = null;
  await vatar.match.leave().catch(() => {});
}

function watch(id: string): void {
  if (watching === id) return;
  off?.();
  off = null;
  watching = id;
  told = false;
  vatar.match
    .onUpdate((u) => {
      if (u.id !== id) return;
      if (u.status === 'playing' && !told) {
        told = true;
        api.bindStake(id).catch(() => {
          told = false; // the next update tries again
        });
      }
      if (u.status === 'open' || u.status === 'playing') table(u);
      else {
        off?.();
        off = null;
        watching = null;
        ended(u);
      }
    })
    .then((o) => {
      if (watching === id) off = o;
      else o();
    })
    .catch(() => {});
}

/**
 * Lagos Life allows one match at a time in a game: leave one that hasn't started (its stake comes
 * back) before opening or joining another. One already in play stays; that one says so itself.
 */
export async function leaveUnstarted(keep?: (m: VatarMatchView) => boolean): Promise<VatarMatchView | null> {
  const m = await vatar.match.current().catch(() => null);
  if (m?.status !== 'open') return m?.status === 'playing' ? m : null;
  if (keep?.(m)) return m;
  await vatar.match.leave();
  // Lagos Life takes one match call every few seconds
  await new Promise((r) => setTimeout(r, 5_100));
  return null;
}
